import React from 'react';
import { QcRequestState } from './qcRequestState';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    CircularProgress,
    LinearProgress,
    Paper,
    Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { Translate } from 'react-localize-redux';
import { VisitReviewProgress } from './reviewProgress';
import { CorrectionFlagState, ReviewerFlagStep, isFlagOpen, reviewerStepsFor } from './correctionFlag';

/**
 * Where the visit is with respect to approval.
 *
 * `approved` carries the attribution rather than a bare flag: an approval is a named
 * person's clinical sign-off, and the screen that renders the lock is the screen where
 * anyone would ask who applied it.
 */
export type ApprovalState =
    | QcRequestState
    | { status: 'approved'; approvedBy: string | null; approvedAt: string | null };

interface ReviewRailProps {
    progress: VisitReviewProgress;
    /** Whether this reviewer holds `VIEW_IDENTIFIABLE_IMAGES`. Changes what the curtain note can promise. */
    canViewIdentifiable: boolean;
    approval: ApprovalState;
    /**
     * The active language code — the container passes `activeLanguage.code`.
     *
     * It exists for one line, the approval date, and that line is worth it:
     * `toLocaleDateString()` with no argument formats in the **browser's** locale, so a
     * French reviewer would read a clinical sign-off dated in whatever their OS is set to.
     *
     * A prop rather than `withLocalize` on this component — the rail is presentational and
     * both the story and the test render it directly, with no localize context to read an
     * active language from.
     *
     * **Not `fullDateFromPostgresString`**, which is the repo's canonical formatter and would
     * otherwise be the right call. It lives in the `src/utils` barrel, and importing that
     * barrel here pulls in `jose` — pure ESM that jest cannot parse — which stops this
     * component's whole suite from running. Reaching the helper would mean changing
     * `transformIgnorePatterns` for every suite in the repo, which is not this ticket's to
     * do. The defect being fixed is the missing locale, and that is fixed.
     */
    localeCode: string;
    onApprove: () => void;
    /**
     * SKIN-577 — the state of the visit's correction flag, null when it was never flagged.
     *
     * Always passed, unlike the actions below: an open flag blocks approval on the server
     * whether or not the study still allows correction flags, so the rail states the block
     * from the flag itself.
     */
    correctionFlagState: CorrectionFlagState | null;
    /**
     * SKIN-577 — the reviewer's steps on the flag. Present only where the study allows
     * correction flags (`WEB_CLINICAL_CORRECTION`); absent, the rail offers none.
     */
    correctionActions?: {
        /** The visit's served count. The API refuses Resolve while it is above zero. */
        undiagnosedLesionCount: number;
        onStep: (step: ReviewerFlagStep) => void;
    };
}

/**
 * SKIN-242 — the visit's progress, its warnings, and the one action that ends the review.
 *
 * It holds no counts. `progress` is recomputed from the marks on the cards below on every
 * render, so the rail cannot claim a total the gallery disagrees with — the one number on
 * this screen a reviewer has no way to check by eye.
 */
export default function ReviewRail({
    progress,
    canViewIdentifiable,
    approval,
    localeCode,
    onApprove,
    correctionFlagState,
    correctionActions,
}: ReviewRailProps) {
    const approved = approval.status === 'approved';
    /**
     * SKIN-577 — while the visit is under correction it cannot be approved: the API refuses
     * with `VISIT_FLAGGED_FOR_CORRECTION`. Disabled here with the reason stated, for the same
     * reason the incomplete count is: a dead button has to say why.
     */
    const flagged = isFlagOpen(correctionFlagState) && !approved;
    const submitting = approval.status === 'submitting';
    /**
     * Approve is **disabled until every image is marked**.
     *
     * That makes the banner below it non-negotiable rather than a nicety. A disabled
     * control cannot be clicked, so it can never be asked why it is disabled — and the
     * answer here is some number of unmarked cards somewhere down a gallery that does not
     * fit on one screen, which the reviewer has no other way to discover. The count is
     * therefore shown for as long as the button is dead, not in response to an attempt
     * that the disabled state has already made impossible.
     */
    const incomplete = !progress.complete && !approved;

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                width: 280,
                flexShrink: 0,
                position: 'sticky',
                // Below the dashboard header, and pinned so the action stays reachable from
                // anywhere in a gallery that runs to 20+ cards.
                top: 16,
                alignSelf: 'flex-start',
            }}
        >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <Translate id="hospital.quality-check.review.rail-title" />
            </Typography>

            <Typography variant="body2" data-testid="review-progress-summary">
                {progress.assessed}
                {' '}
                <Translate id="hospital.quality-check.review.progress-of" />
                {` ${progress.total} `}
                <Translate id="hospital.quality-check.review.progress-assessed" />
            </Typography>

            <LinearProgress
                variant="determinate"
                // A visit with no images has no proportion to show. Dividing by zero would
                // render an indeterminate-looking bar on a visit that is already complete.
                value={progress.total === 0 ? 100 : (progress.assessed / progress.total) * 100}
                data-testid="review-progress-bar"
                sx={{ mt: 1, mb: 2 }}
            />

            {approved && (
                <Alert severity="success" icon={<LockOutlinedIcon fontSize="inherit" />} data-testid="banner-approved" sx={{ mb: 2 }}>
                    <AlertTitle sx={{ mb: 0 }}>
                        <Translate id="hospital.quality-check.review.approved-title" />
                    </AlertTitle>
                    <Translate id="hospital.quality-check.review.approved-lock" />
                    {/*
                      * Each half renders on its own evidence. "Approved by" followed by
                      * nothing reads as a rendering fault on a clinical sign-off, so the
                      * name is conditional — but gating the DATE on the name as well threw
                      * away a fact the API did supply, and the union permits that pair: an
                      * approval whose actor was later removed still has a timestamp, and
                      * when it happened is the more useful of the two.
                      */}
                    {(approval.approvedBy || approval.approvedAt) && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }} data-testid="approved-attribution">
                            {approval.approvedBy && (
                                <React.Fragment>
                                    <Translate id="hospital.quality-check.review.approved-by" />
                                    {` ${approval.approvedBy}`}
                                </React.Fragment>
                            )}
                            {approval.approvedAt && (
                                `${approval.approvedBy ? ', ' : ''}${new Date(approval.approvedAt).toLocaleDateString(localeCode)}`
                            )}
                        </Typography>
                    )}
                </Alert>
            )}

            {/*
              * Shown for as long as Approve is dead. This is the disabled button's only
              * voice: without it the rail offers a control that does nothing and gives no
              * reason, and the reviewer's next move — find the unmarked cards — is exactly
              * what the count tells them the size of.
              */}
            {incomplete && (
                <Alert severity="warning" data-testid="banner-incomplete" sx={{ mb: 2 }}>
                    <AlertTitle sx={{ mb: 0 }}>
                        <Translate id="hospital.quality-check.review.unmarked-label" />
                        {`: ${progress.total - progress.assessed}`}
                    </AlertTitle>
                    <Translate id="hospital.quality-check.review.incomplete-help" />
                </Alert>
            )}

            {progress.curtained > 0 && (
                <Alert severity="info" data-testid="banner-curtained" sx={{ mb: 2 }}>
                    <AlertTitle sx={{ mb: 0 }}>
                        <Translate id="hospital.quality-check.review.curtained-label" />
                        {`: ${progress.curtained}`}
                    </AlertTitle>
                    <Translate
                        id={canViewIdentifiable
                            ? 'hospital.quality-check.review.curtained-note'
                            : 'hospital.quality-check.review.curtained-restricted-note'}
                    />
                    {/*
                      * The promise that the covered images are still this reviewer's to mark
                      * is a separate sentence because it stops being true under the lock. A
                      * restricted reviewer sees covered cards they cannot open and is still
                      * expected to mark all of them, and without saying so the rail's own
                      * count reads as a target they are barred from reaching — but on an
                      * approved visit the cards are static text, and the promise would send
                      * the reviewer hunting for a control that no longer exists.
                      *
                      * The count itself survives approval: it is why the cards render covered
                      * on a locked visit, which is true information and stays.
                      */}
                    {!approved && (
                        <React.Fragment>
                            {' '}
                            <Translate id="hospital.quality-check.review.curtained-markable" />
                        </React.Fragment>
                    )}
                </Alert>
            )}

            {flagged && (
                <Alert severity="warning" icon={<FlagOutlinedIcon fontSize="inherit" />} data-testid="banner-flagged" sx={{ mb: 2 }}>
                    <AlertTitle sx={{ mb: 0 }}>
                        <Translate id="hospital.quality-check.correction.rail.flagged-title" />
                    </AlertTitle>
                    <Translate id="hospital.quality-check.correction.rail.flagged-help" />
                </Alert>
            )}

            {approval.status === 'failed' && (
                <Alert severity="error" data-testid="banner-approve-failed" sx={{ mb: 2 }}>
                    <Translate id="hospital.quality-check.review.approve-failed" />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {approval.error}
                    </Typography>
                </Alert>
            )}

            {/*
              * No action once LOCKED — removed, not disabled. A disabled Approve on an
              * approved visit suggests a state in which it would become available again,
              * and there is none: the lock holds for everyone including the approver.
              */}
            {!approved && (
                <Box>
                    <Button
                        fullWidth
                        variant="contained"
                        disabled={submitting || incomplete || flagged}
                        onClick={onApprove}
                        data-testid="approve-visit"
                        data-label={progress.hasFeedback ? 'with-comments' : 'plain'}
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        <Translate
                            id={submitting
                                ? 'hospital.quality-check.review.approving'
                                : progress.hasFeedback
                                    ? 'hospital.quality-check.review.approve-with-comments'
                                    : 'hospital.quality-check.review.approve'}
                        />
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }} data-testid="approve-irreversible">
                        <Translate id="hospital.quality-check.review.approve-irreversible" />
                    </Typography>
                    {correctionActions && (
                        <CorrectionFlagActions
                            state={correctionFlagState}
                            undiagnosedLesionCount={correctionActions.undiagnosedLesionCount}
                            onStep={correctionActions.onStep}
                        />
                    )}
                </Box>
            )}
        </Paper>
    );
}

/**
 * SKIN-577 — the reviewer's steps on the correction flag, below Approve.
 *
 * Only the steps the flag's state allows (`reviewerStepsFor`, the API's transition map), so
 * the rail never offers one the server answers with `INVALID_TRANSITION`. Outlined, not
 * filled: Approve stays the rail's one primary action, and these are its alternatives.
 */
function CorrectionFlagActions({ state, undiagnosedLesionCount, onStep }: {
    state: CorrectionFlagState | null;
    undiagnosedLesionCount: number;
    onStep: (step: ReviewerFlagStep) => void;
}) {
    const steps = reviewerStepsFor(state);
    /** The API refuses Resolve while a lesion is undiagnosed (`UNDIAGNOSED_LESIONS`), counted as the queue counts. */
    const resolveBlocked = undiagnosedLesionCount > 0;

    return (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {steps.map((step) => (
                <Button
                    key={step}
                    fullWidth
                    variant="outlined"
                    color={step === 'RESOLVE' ? 'success' : 'warning'}
                    startIcon={<FlagOutlinedIcon />}
                    disabled={step === 'RESOLVE' && resolveBlocked}
                    onClick={() => onStep(step)}
                    data-testid={`flag-step-${step}`}
                >
                    <Translate id={`hospital.quality-check.correction.rail.step.${step}`} />
                </Button>
            ))}
            {steps.includes('RESOLVE') && resolveBlocked && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    data-testid="resolve-blocked-undiagnosed"
                    data-undiagnosed={undiagnosedLesionCount}
                >
                    <Translate
                        id="hospital.quality-check.correction.rail.resolve-blocked"
                        data={{ undiagnosed: undiagnosedLesionCount }}
                    />
                </Typography>
            )}
        </Box>
    );
}
